import React from 'react';
import { faTheaterMasks } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/tale-of-three-agents.jpg
import threeAgentsImg from '../../../assets/images/blogs/tall-of-3-Agents.png';

const TailOfThreeAgents = () => (
    <BlogPost
        title="A Tail of Three Agents"
        subtitle="The real magic happens when you combine them - use the Artist to design the tracks, then let the Conductor run them."
        category="Architecture"
        categoryIcon={faTheaterMasks}
        color="#003366"
        date="February 2026"
        readTime="5 min read"
        image={threeAgentsImg}
        imageAlt="Three agent types - Artist, Athlete, and Conductor"
        ctaHeading="Want agents you can trust?"
        ctaText="Book a discovery call and we'll show you how to combine agent types for deterministic, auditable AI."
        seoDescription="Three categories of AI agent design: Creative (Artist), Dynamic (Athlete), and Controlled (Conductor) - and when to use each."
        seoKeywords="AI agents, agent design, ConsistentAI, creative AI, agentic systems, AgentDOG, Nebulyx"
        slug="tail-of-three-agents"
    >
        <p>
            I've been building agentic AI solutions for a while now, and during my testing
            for a specific regulatory use-case I've tried 3 different designs.
        </p>

        <p>
            My work has included using standard Agentic frameworks like LangChain, and LLMs
            like SaaS (OpenAI, Claude etc) as well as OSS (Currently using Qwen 3 models -
            they're very good).
        </p>

        <p>
            But my current use-case requires transparency and determinism so I've found these
            not to fit my requirements on their own. But that is not to say one can't use
            them (many do).
        </p>

        <p>
            In my work I've found it useful to classify Agent design into 3 categories, based
            on key aspects the specific approach is good for:
        </p>

        <h3>🎨 The Artist (Creative Agent)</h3>

        <p>
            Pure LLM. Give it tools, give it a goal, let it improvise.
        </p>

        <p>
            ✅ Incredibly flexible<br />
            ✅ Handles novel situations<br />
            ✅ Fast to prototype
        </p>

        <p>
            ❌ Hallucinates (confidently wrong)<br />
            ❌ Same input, different output<br />
            ❌ "Why did it do that?" Opaque
        </p>

        <p>
            <strong>Best for:</strong> Brainstorming, research, one-off tasks where creativity
            beats consistency and transparency.
        </p>

        <h3>🏀 The Athlete (Dynamic Agent)</h3>

        <p>
            Follows a state-based approach, discovers new states and actions as it goes.
            Learning on the court.
        </p>

        <p>
            ✅ Adapts to the unexpected<br />
            ✅ Gets smarter over time<br />
            ✅ More structured than pure improv
        </p>

        <p>
            ❌ Can still go off-piste<br />
            ❌ Harder to predict<br />
            ❌ The playbook keeps changing
        </p>

        <p>
            <strong>Best for:</strong> Exploration, R&amp;D, Financial Trading - domains where
            you don't know all the moves upfront.
        </p>

        <h3>🚂 The Conductor (Controlled Agent)</h3>

        <p>
            States and actions defined at design time. AI can help create the tracks, but
            once deployed - it runs on rails.
        </p>

        <p>
            ✅ Consistent- same input, same output<br />
            ✅ Fully auditable<br />
            ✅ Compliant (prove what it will do)<br />
            ✅ Fast (no LLM at runtime)
        </p>

        <p>
            ❌ Can't improvise<br />
            ❌ Needs upfront design<br />
            ❌ Changes need redeployment
        </p>

        <p>
            <strong>Best for:</strong> Regulation, Healthcare, finance, legal - anywhere
            "why?" matters.
        </p>

        <h3>Choosing the Right Agent</h3>

        <p>
            Most teams default to the Artist (Creative Agent) because it demos well and can
            be easily used with SaaS LLMs. But be prepared to do a lot of work to catch the
            edge-cases and achieve consistent accuracy.
        </p>

        <p>
            The Athlete is very good when the environment and state space isn't clear but
            you still need transparency - e.g. financial trading.
        </p>

        <p>
            But if you need to operate in regulated domains? You want the Conductor.
        </p>

        <h3>The Real Magic</h3>

        <blockquote className="blog-post-quote">
            "The real magic happens when you combine them - use the Artist to design the
            tracks, then let the Conductor run them. Agents-on-rails."
        </blockquote>

        <p>
            And Data Object Graphs tying it all together.
        </p>

        <p>
            Best of all worlds. 🎨→🚂 🎯
        </p>
    </BlogPost>
);

export default TailOfThreeAgents;
